import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets
 * @property {HTMLElement} [overflowList]
 */

/**
 * @typedef {object} LocalVariant
 * @property {string | number} id
 * @property {string} title
 * @property {boolean} available
 * @property {string[]} options
 * @property {number} price
 * @property {string} priceFormatted
 * @property {number | null} compareAtPrice
 * @property {string | null} compareAtPriceFormatted
 * @property {string | number | null} featuredMediaId
 * @property {string | null} image64
 * @property {string | null} image96
 * @property {string | null} image128
 * @property {string | null} image800
 * @property {string | null} image1200
 */

/**
 * Product variant picker.
 *
 * Product-page behavior:
 * - Resolves the complete variant locally from all selected option values.
 * - Updates the main media immediately for every option change, not only the image option.
 * - Treats the final option as the image-swatch option, regardless of its merchant-defined name.
 * - Refreshes final-option thumbnails whenever an earlier option changes.
 * - Keeps Shopify section rendering in the background for inventory, selling plans,
 *   pickup availability, SKU, dynamic checkout, and other theme components.
 * - Avoids morphing the picker during the local product-page path, preventing stale
 *   swatch data, click races, and visible picker rebuilds.
 *
 * Other contexts retain the original DWELL request/morph behavior:
 * - Product cards
 * - Quick add
 * - Featured product sections
 * - Combined listings that load another product URL
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {LocalVariant[]} */
  #variants = [];

  /** @type {string | undefined} */
  #lastSelectedOptionId;

  /** @type {Map<string, Promise<HTMLImageElement>>} */
  #imagePreloadCache = new Map();

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #handleChange = (event) => this.variantChanged(event);

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const input = event.target.closest(
      '.variant-option--image-swatches input[data-variant-image-1200], .variant-option--image-swatches input[data-variant-image-800]'
    );

    if (!(input instanceof HTMLInputElement)) return;

    const imageUrl = input.dataset.variantImage1200 || input.dataset.variantImage800;
    if (imageUrl) this.#preloadImage(imageUrl).catch(() => {});
  };

  connectedCallback() {
    super.connectedCallback();

    this.#initializePickerState();
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('pointerover', this.#handlePreloadIntent);
    this.addEventListener('focusin', this.#handlePreloadIntent);
    this.addEventListener('touchstart', this.#handlePreloadIntent, { passive: true });

    this.#resizeObserver.observe(this);
    this.#refreshLocalOptionState();
    this.#preloadInitialMedia();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('pointerover', this.#handlePreloadIntent);
    this.removeEventListener('focusin', this.#handlePreloadIntent);
    this.removeEventListener('touchstart', this.#handlePreloadIntent);

    this.#abortController?.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles a product option change.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!(selectedOption instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.#lastSelectedOptionId = selectedOption.dataset.optionValueId;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && Boolean(newUrl) && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const useLocalEngine = this.#shouldUseLocalVariantEngine(
      isOnProductPage,
      loadsNewProduct,
      isOnFeaturedProductSection
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';

    if (useLocalEngine) {
      const variant = this.#findSelectedVariant();

      this.#refreshLocalOptionState();

      if (variant) {
        this.#applyLocalVariant(variant, selectedOption);
        this.#updateBrowserUrl(String(variant.id), true, false, undefined);
      } else {
        this.#applyUnavailableCombinationState();
        this.#updateBrowserUrl(null, true, false, undefined);
      }

      // Keep the rest of the theme synchronized without rebuilding this picker.
      this.fetchUpdatedSection(
        this.buildRequestUrl(selectedOption),
        morphElementSelector,
        optionValueId,
        true
      );

      return;
    }

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector,
      optionValueId,
      false
    );

    this.#updateBrowserUrl(
      selectedOption.dataset.variantId || null,
      isOnProductPage,
      loadsNewProduct,
      newUrl
    );
  }

  /**
   * @param {boolean} isOnProductPage
   * @param {boolean} loadsNewProduct
   * @param {boolean} isOnFeaturedProductSection
   * @returns {boolean}
   */
  #shouldUseLocalVariantEngine(isOnProductPage, loadsNewProduct, isOnFeaturedProductSection) {
    if (!isOnProductPage) return false;
    if (loadsNewProduct) return false;
    if (isOnFeaturedProductSection) return false;
    if (!this.#variants.length) return false;

    return true;
  }

  /**
   * Rebuilds local control and variant caches.
   */
  #initializePickerState() {
    this.#checkedIndices = [];
    this.#radios = [];
    this.#variants = this.#readLocalVariants();

    const fieldsets = this.#getFieldsets();

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );

      this.#checkedIndices.push(initialCheckedIndex >= 0 ? [initialCheckedIndex] : []);
    });
  }

  /**
   * @returns {HTMLFieldSetElement[]}
   */
  #getFieldsets() {
    return Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
  }

  /**
   * @returns {LocalVariant[]}
   */
  #readLocalVariants() {
    const script = this.querySelector('script[data-variant-picker-variants]');
    const text = script?.textContent?.trim();

    if (!text) return [];

    try {
      const variants = JSON.parse(text);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('Unable to parse local variant data.', error);
      return [];
    }
  }

  /**
   * @returns {string[]}
   */
  #getSelectedOptionValuesInOrder() {
    const wrappers = Array.from(this.querySelectorAll('[data-variant-option]')).sort((a, b) => {
      const aPosition = Number(a.getAttribute('data-option-position') || 0);
      const bPosition = Number(b.getAttribute('data-option-position') || 0);
      return aPosition - bPosition;
    });

    return wrappers.map((wrapper) => {
      const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) return checkedInput.value;

      const select = wrapper.querySelector('select');
      if (select instanceof HTMLSelectElement) return select.value;

      const hiddenInput = wrapper.querySelector('input[data-single-option]');
      if (hiddenInput instanceof HTMLInputElement) return hiddenInput.value;

      return wrapper.getAttribute('data-selected-value') || '';
    });
  }

  /**
   * @param {string[]} optionValues
   * @returns {LocalVariant | undefined}
   */
  #findVariantByOptions(optionValues) {
    if (!optionValues.length) return undefined;

    return this.#variants.find((variant) => {
      if (!Array.isArray(variant.options) || variant.options.length !== optionValues.length) return false;
      return variant.options.every((value, index) => value === optionValues[index]);
    });
  }

  /**
   * @returns {LocalVariant | undefined}
   */
  #findSelectedVariant() {
    return this.#findVariantByOptions(this.#getSelectedOptionValuesInOrder());
  }

  /**
   * Updates option availability and final-option image data from the complete local variant set.
   */
  #refreshLocalOptionState() {
    if (!this.#variants.length) return;

    this.#refreshOptionAvailability();
    this.#refreshLastOptionSwatches();
    this.#updateCurrentOptionLabels();
  }

  /**
   * Shopify-style progressive availability:
   * - Earlier selected values must match.
   * - The candidate value at the current position must match.
   * - Later options are ignored.
   */
  #refreshOptionAvailability() {
    const selectedValues = this.#getSelectedOptionValuesInOrder();
    const wrappers = Array.from(this.querySelectorAll('[data-variant-option]'));

    wrappers.forEach((wrapper) => {
      const optionPosition = Number(wrapper.getAttribute('data-option-position') || 0);
      const optionIndex = optionPosition - 1;

      const controls = wrapper.querySelectorAll('input[type="radio"], option');

      controls.forEach((control) => {
        if (!(control instanceof HTMLInputElement || control instanceof HTMLOptionElement)) return;

        const candidateValue = control.value;

        const matchingVariants = this.#variants.filter((variant) => {
          if (!Array.isArray(variant.options)) return false;

          for (let index = 0; index < optionIndex; index += 1) {
            if (variant.options[index] !== selectedValues[index]) return false;
          }

          return variant.options[optionIndex] === candidateValue;
        });

        const exists = matchingVariants.length > 0;
        const available = matchingVariants.some((variant) => variant.available);

        control.dataset.optionAvailable = available ? 'true' : 'false';
        control.toggleAttribute('aria-disabled', !available);

        if (control instanceof HTMLOptionElement) {
          control.disabled = !exists;
        }
      });
    });
  }

  /**
   * Rebinds every final-option swatch to the exact variant produced by the
   * currently selected earlier options plus that swatch value.
   */
  #refreshLastOptionSwatches() {
    const lastOption = this.querySelector('[data-variant-option][data-is-last-option="true"]');
    if (!(lastOption instanceof HTMLElement)) return;

    const optionPosition = Number(lastOption.dataset.optionPosition || 0);
    const optionIndex = optionPosition - 1;
    const selectedValues = this.#getSelectedOptionValuesInOrder();

    lastOption.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const candidateValues = [...selectedValues];
      candidateValues[optionIndex] = input.value;

      const variant = this.#findVariantByOptions(candidateValues);
      this.#writeVariantDataToControl(input, variant);
      this.#updateSwatchImage(input, variant);
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @param {LocalVariant | undefined} variant
   */
  #writeVariantDataToControl(input, variant) {
    const variantDatasetKeys = [
      'variantId',
      'variantPrice',
      'variantPriceRaw',
      'variantComparePrice',
      'variantComparePriceRaw',
      'variantSoldOut',
      'variantMediaId',
      'variantImage64',
      'variantImage96',
      'variantImage128',
      'variantImage800',
      'variantImage1200',
    ];

    if (!variant) {
      variantDatasetKeys.forEach((key) => delete input.dataset[key]);
      input.dataset.optionAvailable = 'false';
      input.setAttribute('aria-disabled', 'true');
      return;
    }

    input.dataset.variantId = String(variant.id);
    input.dataset.variantPrice = variant.priceFormatted || '';
    input.dataset.variantPriceRaw = String(variant.price ?? 0);
    input.dataset.variantSoldOut = variant.available ? 'false' : 'true';
    input.dataset.optionAvailable = variant.available ? 'true' : 'false';
    input.toggleAttribute('aria-disabled', !variant.available);

    if (variant.compareAtPrice != null) {
      input.dataset.variantComparePrice = variant.compareAtPriceFormatted || '';
      input.dataset.variantComparePriceRaw = String(variant.compareAtPrice);
    } else {
      delete input.dataset.variantComparePrice;
      delete input.dataset.variantComparePriceRaw;
    }

    this.#setOptionalDataset(input, 'variantMediaId', variant.featuredMediaId);
    this.#setOptionalDataset(input, 'variantImage64', variant.image64);
    this.#setOptionalDataset(input, 'variantImage96', variant.image96);
    this.#setOptionalDataset(input, 'variantImage128', variant.image128);
    this.#setOptionalDataset(input, 'variantImage800', variant.image800);
    this.#setOptionalDataset(input, 'variantImage1200', variant.image1200);
  }

  /**
   * @param {HTMLElement} element
   * @param {string} key
   * @param {string | number | null | undefined} value
   */
  #setOptionalDataset(element, key, value) {
    if (value === null || value === undefined || value === '') {
      delete element.dataset[key];
      return;
    }

    element.dataset[key] = String(value);
  }

  /**
   * @param {HTMLInputElement} input
   * @param {LocalVariant | undefined} variant
   */
  #updateSwatchImage(input, variant) {
    const label = input.closest('label');
    if (!(label instanceof HTMLElement)) return;

    const imageWrap = label.querySelector('.variant-option__color-image-wrap');
    if (!(imageWrap instanceof HTMLElement)) return;

    const image = imageWrap.querySelector('img');
    const fallback = imageWrap.querySelector('.variant-option__color-image-fallback');

    if (!variant?.image128) {
      image?.remove();

      if (!fallback) {
        const fallbackElement = document.createElement('span');
        fallbackElement.className = 'variant-option__color-image-fallback';
        fallbackElement.textContent = input.value;
        imageWrap.appendChild(fallbackElement);
      }

      return;
    }

    fallback?.remove();

    const targetImage = image instanceof HTMLImageElement ? image : document.createElement('img');
    targetImage.src = variant.image128;
    targetImage.srcset = [
      variant.image64 ? `${variant.image64} 64w` : '',
      variant.image96 ? `${variant.image96} 96w` : '',
      variant.image128 ? `${variant.image128} 128w` : '',
    ]
      .filter(Boolean)
      .join(', ');
    targetImage.sizes = '64px';
    targetImage.alt = input.value;
    targetImage.width = 64;
    targetImage.height = 64;
    targetImage.loading = 'eager';
    targetImage.decoding = 'async';
    targetImage.fetchPriority = 'low';
    targetImage.className = 'variant-option__color-image';

    if (!targetImage.isConnected) imageWrap.appendChild(targetImage);
  }

  /**
   * @param {LocalVariant} variant
   * @param {HTMLElement} selectedOption
   */
  #applyLocalVariant(variant, selectedOption) {
    this.dataset.currentVariantId = String(variant.id);

    this.#writeVariantDataToElement(selectedOption, variant);
    this.#updateCurrentOptionLabels();
    this.#updateCartVariantInputs(variant);
    this.#updatePrice(variant);
    this.#updateAddToCartState(variant.available);

    const gallerySwitched = this.#switchGalleryToVariantMedia(variant);

    if (!gallerySwitched) {
      this.#smoothSwapMainProductImage(variant, selectedOption);
    }

    const imageUrl = variant.image1200 || variant.image800;
    if (imageUrl) this.#preloadImage(imageUrl).catch(() => {});
  }

  /**
   * @param {HTMLElement} element
   * @param {LocalVariant} variant
   */
  #writeVariantDataToElement(element, variant) {
    element.dataset.variantId = String(variant.id);
    element.dataset.variantPrice = variant.priceFormatted || '';
    element.dataset.variantPriceRaw = String(variant.price ?? 0);
    element.dataset.variantSoldOut = variant.available ? 'false' : 'true';

    if (variant.compareAtPrice != null) {
      element.dataset.variantComparePrice = variant.compareAtPriceFormatted || '';
      element.dataset.variantComparePriceRaw = String(variant.compareAtPrice);
    } else {
      delete element.dataset.variantComparePrice;
      delete element.dataset.variantComparePriceRaw;
    }

    this.#setOptionalDataset(element, 'variantMediaId', variant.featuredMediaId);
    this.#setOptionalDataset(element, 'variantImage800', variant.image800);
    this.#setOptionalDataset(element, 'variantImage1200', variant.image1200);
  }

  #applyUnavailableCombinationState() {
    this.#updateAddToCartState(false);
  }

  #updateCurrentOptionLabels() {
    this.querySelectorAll('[data-variant-option]').forEach((wrapper) => {
      const valueElement = wrapper.querySelector('.variant-option__swatch-value');
      if (!(valueElement instanceof HTMLElement)) return;

      const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
      const select = wrapper.querySelector('select');
      const hiddenInput = wrapper.querySelector('input[data-single-option]');

      const value =
        checkedInput instanceof HTMLInputElement
          ? checkedInput.value
          : select instanceof HTMLSelectElement
            ? select.value
            : hiddenInput instanceof HTMLInputElement
              ? hiddenInput.value
              : wrapper.getAttribute('data-selected-value') || '';

      valueElement.textContent = value;
    });
  }

  /**
   * @param {LocalVariant} variant
   */
  #updateCartVariantInputs(variant) {
    const scope = this.#getProductScope();
    const selectors = [
      'form[action*="/cart/add"] input[name="id"]',
      'product-form-component input[name="id"]',
      '.shopify-product-form input[name="id"]',
      'input[ref="variantId"]',
    ];

    const inputs = new Set();

    selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((input) => inputs.add(input));
    });

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const nextValue = String(variant.id);
      if (input.value === nextValue) return;

      input.value = nextValue;
      input.setAttribute('value', nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * @param {LocalVariant} variant
   */
  #updatePrice(variant) {
    if (!variant.priceFormatted) return;

    const scope = this.#getProductScope();
    const priceBlocks = new Set();

    scope.querySelectorAll('product-price').forEach((element) => priceBlocks.add(element));

    if (this.dataset.productId) {
      document
        .querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`)
        .forEach((element) => {
          if (element.closest('product-card') || element.closest('quick-add-dialog')) return;
          priceBlocks.add(element);
        });
    }

    priceBlocks.forEach((priceBlock) => {
      if (!(priceBlock instanceof HTMLElement)) return;

      const comparePrice = variant.compareAtPriceFormatted;
      const hasComparePrice =
        variant.compareAtPrice != null &&
        Number(variant.compareAtPrice) > Number(variant.price);

      const salePriceElement =
        priceBlock.querySelector('.price-item--sale') ||
        priceBlock.querySelector('.price__sale .price') ||
        priceBlock.querySelector('[data-sale-price]');

      const regularPriceElements = priceBlock.querySelectorAll(
        '.price-item--regular, .compare-at-price, [data-compare-price]'
      );

      const basicPriceElement =
        priceBlock.querySelector('.price__regular .price') ||
        priceBlock.querySelector('[data-price]') ||
        priceBlock.querySelector('.price');

      if (hasComparePrice && comparePrice) {
        if (salePriceElement) salePriceElement.textContent = variant.priceFormatted;

        regularPriceElements.forEach((element) => {
          element.textContent = comparePrice;
          element.removeAttribute('hidden');
          element.classList.remove('hidden');
        });

        priceBlock.querySelector('.price__regular')?.classList.add('price__hidden');
        priceBlock.querySelector('.price__sale')?.classList.remove('price__hidden');
      } else {
        if (basicPriceElement) basicPriceElement.textContent = variant.priceFormatted;
        if (salePriceElement) salePriceElement.textContent = variant.priceFormatted;

        regularPriceElements.forEach((element) => {
          if (element === basicPriceElement) return;
          element.textContent = '';
          element.setAttribute('hidden', 'hidden');
          element.classList.add('hidden');
        });

        priceBlock.querySelector('.price__regular')?.classList.remove('price__hidden');
        priceBlock.querySelector('.price__sale')?.classList.add('price__hidden');
      }
    });
  }

  /**
   * Updates only state attributes locally. The background section response keeps
   * translated button text and any theme-specific markup authoritative.
   * @param {boolean} available
   */
  #updateAddToCartState(available) {
    const scope = this.#getProductScope();
    const buttons = scope.querySelectorAll(
      'button[name="add"], .add-to-cart-button, [data-testid="standalone-add-to-cart"]'
    );

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = !available;
      button.toggleAttribute('disabled', !available);
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
    });
  }

  /**
   * @param {LocalVariant} variant
   * @returns {boolean}
   */
  #switchGalleryToVariantMedia(variant) {
    if (variant.featuredMediaId === null || variant.featuredMediaId === undefined) return false;

    const mediaId = String(variant.featuredMediaId);
    const scope = this.#getProductScope();
    const galleries = this.#getProductMediaGalleries(scope);

    for (const gallery of galleries) {
      if (typeof gallery.selectMediaById !== 'function') continue;

      try {
        const result = gallery.selectMediaById(mediaId, {
          animate: false,
          centerThumbnail: true,
        });

        if (result) return true;
      } catch (error) {
        console.warn('media-gallery.selectMediaById failed; using fallback.', error);
      }
    }

    return this.#fallbackSwitchGalleryToMedia(mediaId);
  }

  /**
   * @param {ParentNode} scope
   * @returns {HTMLElement[]}
   */
  #getProductMediaGalleries(scope) {
    const galleries = [
      ...scope.querySelectorAll('media-gallery'),
      ...document.querySelectorAll('media-gallery'),
    ].filter((gallery) => {
      if (!(gallery instanceof HTMLElement)) return false;
      if (gallery.closest('product-card')) return false;
      if (gallery.closest('quick-add-dialog')) return false;
      return true;
    });

    return /** @type {HTMLElement[]} */ ([...new Set(galleries)]);
  }

  /**
   * @param {string} mediaId
   * @returns {boolean}
   */
  #fallbackSwitchGalleryToMedia(mediaId) {
    const scope = this.#getProductScope();
    const galleries = this.#getProductMediaGalleries(scope);

    for (const gallery of galleries) {
      if (this.#fallbackSwitchSingleGalleryToMedia(gallery, mediaId)) return true;
    }

    return this.#fallbackSwitchSingleGalleryToMedia(scope, mediaId);
  }

  /**
   * @param {ParentNode} scope
   * @param {string} mediaId
   * @returns {boolean}
   */
  #fallbackSwitchSingleGalleryToMedia(scope, mediaId) {
    if (!(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) return false;

    const mediaElement = scope.querySelector(
      `[data-media-id="${mediaId}"], [data-media-id$="-${mediaId}"], [data-product-media-id="${mediaId}"]`
    );

    if (!(mediaElement instanceof HTMLElement)) return false;

    const target =
      mediaElement.closest('[ref="media[]"]') ||
      mediaElement.closest('slideshow-slide') ||
      mediaElement.closest('.slideshow-slide') ||
      mediaElement.closest('.product-media-container') ||
      mediaElement.closest('.product-media') ||
      mediaElement;

    if (!(target instanceof HTMLElement)) return false;

    this.#selectSlideshowTarget(target, mediaId);
    this.#syncGalleryThumbnailState(scope, mediaId, target);

    return true;
  }

  /**
   * @param {HTMLElement} target
   * @param {string} mediaId
   */
  #selectSlideshowTarget(target, mediaId) {
    const gallery = target.closest('media-gallery');
    const slideshow = gallery?.querySelector(
      'slideshow-component, slideshow-container, slideshow-slider'
    );

    const mediaItems = Array.from(
      gallery?.querySelectorAll(
        '[ref="media[]"], slideshow-slide, .slideshow-slide, .product-media-container, .product-media'
      ) || []
    ).filter((item) => item instanceof HTMLElement);

    const targetIndex = mediaItems.findIndex((item) => {
      if (!(item instanceof HTMLElement)) return false;

      return (
        item === target ||
        item.contains(target) ||
        Boolean(
          item.querySelector(
            `[data-media-id="${mediaId}"], [data-media-id$="-${mediaId}"], [data-product-media-id="${mediaId}"]`
          )
        )
      );
    });

    if (gallery && gallery.slideshow && typeof gallery.slideshow.select === 'function' && targetIndex >= 0) {
      gallery.slideshow.select(targetIndex, undefined, { animate: false });
      return;
    }

    if (slideshow && typeof slideshow.select === 'function' && targetIndex >= 0) {
      slideshow.select(targetIndex, undefined, { animate: false });
      return;
    }

    target.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  /**
   * @param {ParentNode} scope
   * @param {string} mediaId
   * @param {HTMLElement} target
   */
  #syncGalleryThumbnailState(scope, mediaId, target) {
    if (!(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) return;

    const gallery = target.closest('media-gallery') || scope;
    const candidates = gallery.querySelectorAll(
      [
        '.thumbnail-list [data-media-id]',
        '.thumbnail-list [data-target]',
        '.thumbnail-list [aria-controls]',
        '.thumbnail-list [data-slide-index]',
        '.thumbnail-list [data-index]',
        '.thumbnail-list button',
        '.thumbnail-list a',
        '[class*="thumbnail"] [data-media-id]',
        '[class*="thumbnail"] [data-target]',
        '[class*="thumbnail"] [aria-controls]',
        '[class*="thumbnail"] [data-slide-index]',
        '[class*="thumbnail"] [data-index]',
        '[class*="thumbnail"] button',
        '[class*="thumbnail"] a',
        'slideshow-controls [data-media-id]',
        'slideshow-controls [data-target]',
        'slideshow-controls [aria-controls]',
        'slideshow-controls [data-slide-index]',
        'slideshow-controls [data-index]',
        'slideshow-controls button',
        'slideshow-controls a',
      ].join(',')
    );

    let activeThumbnail = null;

    candidates.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      if (thumbnail.closest('.variant-option--image-swatches')) return;

      const values = [
        thumbnail.dataset.mediaId,
        thumbnail.dataset.target,
        thumbnail.dataset.index,
        thumbnail.getAttribute('data-index'),
        thumbnail.dataset.slideIndex,
        thumbnail.getAttribute('data-slide-index'),
        thumbnail.getAttribute('aria-controls'),
        thumbnail.getAttribute('href'),
        thumbnail.getAttribute('for'),
      ].filter(Boolean);

      const hasMediaInside = Boolean(
        thumbnail.querySelector(
          `[data-media-id="${mediaId}"], [data-media-id$="-${mediaId}"], [data-product-media-id="${mediaId}"]`
        )
      );

      const isMatch =
        hasMediaInside ||
        values.some((value) => value === mediaId || value?.endsWith(`-${mediaId}`) || value?.includes(mediaId));

      thumbnail.classList.toggle('is-active', isMatch);
      thumbnail.classList.toggle('active', isMatch);
      thumbnail.classList.toggle('selected', isMatch);
      thumbnail.classList.toggle('is-selected', isMatch);
      thumbnail.toggleAttribute('aria-current', isMatch);

      if (isMatch) {
        thumbnail.setAttribute('aria-selected', 'true');
        activeThumbnail = thumbnail;
      } else {
        thumbnail.removeAttribute('aria-selected');
      }
    });

    if (activeThumbnail instanceof HTMLElement) {
      this.#centerThumbnail(activeThumbnail);
    }
  }

  /**
   * @param {HTMLElement} thumbnail
   */
  #centerThumbnail(thumbnail) {
    const scrollContainer =
      this.#getScrollableParent(thumbnail) ||
      thumbnail.closest('.thumbnail-list') ||
      thumbnail.closest('[class*="thumbnail"]') ||
      thumbnail.parentElement;

    if (!(scrollContainer instanceof HTMLElement)) {
      thumbnail.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();
    const canScrollX = scrollContainer.scrollWidth > scrollContainer.clientWidth + 1;
    const canScrollY = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;

    if (canScrollX) {
      const thumbnailCenter =
        thumbnailRect.left - containerRect.left + scrollContainer.scrollLeft + thumbnailRect.width / 2;

      scrollContainer.scrollTo({
        left: Math.max(0, thumbnailCenter - scrollContainer.clientWidth / 2),
        behavior: 'auto',
      });
      return;
    }

    if (canScrollY) {
      const thumbnailCenter =
        thumbnailRect.top - containerRect.top + scrollContainer.scrollTop + thumbnailRect.height / 2;

      scrollContainer.scrollTo({
        top: Math.max(0, thumbnailCenter - scrollContainer.clientHeight / 2),
        behavior: 'auto',
      });
      return;
    }

    thumbnail.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
  }

  /**
   * @param {HTMLElement} element
   * @returns {HTMLElement | null}
   */
  #getScrollableParent(element) {
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const canScrollX = parent.scrollWidth > parent.clientWidth + 1;
      const canScrollY = parent.scrollHeight > parent.clientHeight + 1;
      const allowsScrollX = ['auto', 'scroll', 'overlay'].includes(style.overflowX);
      const allowsScrollY = ['auto', 'scroll', 'overlay'].includes(style.overflowY);

      if ((canScrollX && allowsScrollX) || (canScrollY && allowsScrollY)) return parent;
      parent = parent.parentElement;
    }

    return null;
  }

  /**
   * @param {LocalVariant} variant
   * @param {HTMLElement} selectedOption
   */
  #smoothSwapMainProductImage(variant, selectedOption) {
    const image800 = variant.image800;
    const image1200 = variant.image1200 || image800;
    const mediaId = variant.featuredMediaId == null ? undefined : String(variant.featuredMediaId);

    if (!image800) return;

    const mainImage = this.#findBestMainProductImage(mediaId);
    if (!mainImage) return;

    const targetUrl = image1200 || image800;
    const currentSrc = mainImage.currentSrc || mainImage.src || '';

    if (currentSrc && this.#normalizeImageUrl(currentSrc) === this.#normalizeImageUrl(targetUrl)) return;

    const mediaFrame = this.#getStableMediaFrame(mainImage);
    const frameRect = mediaFrame.getBoundingClientRect();

    if (frameRect.width < 220 || frameRect.height < 220) return;

    this.#lockMediaFrame(mediaFrame, frameRect);

    this.#preloadImage(targetUrl)
      .then(() => {
        const overlay = document.createElement('img');
        overlay.src = targetUrl;
        overlay.srcset = image1200 ? `${image800} 800w, ${image1200} 1200w` : image800;
        overlay.sizes = mainImage.getAttribute('sizes') || '(min-width: 990px) 50vw, 100vw';
        overlay.alt = selectedOption.getAttribute('aria-label') || mainImage.alt || '';
        overlay.loading = 'eager';
        overlay.decoding = 'async';
        overlay.className = `${mainImage.className || ''} variant-picker-smooth-main-image-overlay`.trim();

        Object.assign(overlay.style, {
          position: 'absolute',
          inset: '0',
          width: '100%',
          height: '100%',
          objectFit: getComputedStyle(mainImage).objectFit || 'cover',
          objectPosition: getComputedStyle(mainImage).objectPosition || 'center',
          opacity: '0',
          zIndex: '3',
          pointerEvents: 'none',
          transform: 'translateZ(0)',
          transition: 'opacity 100ms linear',
          animation: 'none',
        });

        mediaFrame.appendChild(overlay);

        requestAnimationFrame(() => {
          overlay.style.opacity = '1';
        });

        window.setTimeout(() => {
          this.#commitMainImage(mainImage, variant, selectedOption);
          overlay.remove();
          this.#unlockMediaFrame(mediaFrame);
        }, 110);
      })
      .catch(() => {
        this.#commitMainImage(mainImage, variant, selectedOption);
        this.#unlockMediaFrame(mediaFrame);
      });
  }

  /**
   * @param {HTMLImageElement} image
   * @param {LocalVariant} variant
   * @param {HTMLElement} selectedOption
   */
  #commitMainImage(image, variant, selectedOption) {
    const image800 = variant.image800;
    const image1200 = variant.image1200 || image800;
    if (!image800) return;

    const computed = getComputedStyle(image);
    const oldTransition = image.style.transition;
    const oldAnimation = image.style.animation;
    const oldTransform = image.style.transform;
    const width =
      image.getAttribute('width') ||
      String(Math.round(image.getBoundingClientRect().width || image.width || 1200));
    const height =
      image.getAttribute('height') ||
      String(Math.round(image.getBoundingClientRect().height || image.height || 1200));

    image.style.transition = 'none';
    image.style.animation = 'none';
    image.style.transform = 'none';
    image.style.objectFit = computed.objectFit || image.style.objectFit;
    image.style.objectPosition = computed.objectPosition || image.style.objectPosition;

    image.setAttribute('src', image800);
    image.setAttribute('srcset', image1200 ? `${image800} 800w, ${image1200} 1200w` : image800);
    image.setAttribute('sizes', image.getAttribute('sizes') || '(min-width: 990px) 50vw, 100vw');
    image.setAttribute('width', width);
    image.setAttribute('height', height);
    image.setAttribute('alt', selectedOption.getAttribute('aria-label') || image.alt || '');
    image.setAttribute('loading', 'eager');
    image.setAttribute('decoding', 'async');

    const picture = image.closest('picture');
    picture?.querySelectorAll('source').forEach((source) => {
      source.setAttribute('srcset', image1200 ? `${image800} 800w, ${image1200} 1200w` : image800);
    });

    requestAnimationFrame(() => {
      image.style.transition = oldTransition;
      image.style.animation = oldAnimation;
      image.style.transform = oldTransform;
    });
  }

  /**
   * @param {string | undefined} mediaId
   * @returns {HTMLImageElement | null}
   */
  #findBestMainProductImage(mediaId) {
    const scope = this.#getProductScope();

    if (mediaId) {
      const mediaSelectors = [
        `[data-media-id="${mediaId}"] img`,
        `[data-media-id*="${mediaId}"] img`,
        `[data-product-media-id="${mediaId}"] img`,
        `[data-media-id="${this.dataset.productId}-${mediaId}"] img`,
      ];

      for (const selector of mediaSelectors) {
        const image = document.querySelector(selector);
        if (image instanceof HTMLImageElement && this.#isValidMainImageCandidate(image)) return image;
      }
    }

    const candidates = Array.from(
      scope.querySelectorAll(
        [
          'media-gallery img',
          'product-gallery img',
          '.product-gallery img',
          '.product-media img',
          '.product__media img',
          '.product-media-container img',
          '.product__media-wrapper img',
          '[data-product-media] img',
          '[data-testid*="media"] img',
        ].join(',')
      )
    ).filter((image) => image instanceof HTMLImageElement && this.#isValidMainImageCandidate(image));

    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();
      return bRect.width * bRect.height - aRect.width * aRect.height;
    });

    return /** @type {HTMLImageElement} */ (candidates[0]);
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {boolean}
   */
  #isValidMainImageCandidate(image) {
    if (image.closest('variant-picker')) return false;
    if (image.closest('.variant-option--image-swatches')) return false;
    if (image.closest('product-card')) return false;
    if (image.closest('quick-add-dialog')) return false;
    if (image.closest('.thumbnail-list')) return false;
    if (image.closest('.thumbnail')) return false;
    if (image.closest('[class*="thumbnail"]')) return false;

    const rect = image.getBoundingClientRect();
    return rect.width >= 220 && rect.height >= 220;
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {HTMLElement}
   */
  #getStableMediaFrame(image) {
    return (
      image.closest('.product-media-container') ||
      image.closest('.product__media') ||
      image.closest('.product-media') ||
      image.closest('[data-product-media]') ||
      image.parentElement ||
      image
    );
  }

  /**
   * @param {HTMLElement} frame
   * @param {DOMRect} rect
   */
  #lockMediaFrame(frame, rect) {
    if (!frame.dataset.variantPickerOriginalStyle) {
      frame.dataset.variantPickerOriginalStyle = frame.getAttribute('style') || '';
    }

    frame.style.position = frame.style.position || 'relative';
    frame.style.overflow = 'hidden';
    frame.style.minWidth = `${Math.round(rect.width)}px`;
    frame.style.minHeight = `${Math.round(rect.height)}px`;
    frame.style.contain = frame.style.contain || 'layout paint';
  }

  /**
   * @param {HTMLElement} frame
   */
  #unlockMediaFrame(frame) {
    window.setTimeout(() => {
      const originalStyle = frame.dataset.variantPickerOriginalStyle;

      if (originalStyle !== undefined) {
        frame.setAttribute('style', originalStyle);
        delete frame.dataset.variantPickerOriginalStyle;
      }
    }, 50);
  }

  /**
   * @returns {ParentNode}
   */
  #getProductScope() {
    return (
      this.closest('[data-testid="product-information-details"]')?.parentElement ||
      this.closest('product-information') ||
      this.closest('.product') ||
      this.closest('main') ||
      document
    );
  }

  #preloadInitialMedia() {
    const selectedVariant = this.#findSelectedVariant();
    const selectedImage = selectedVariant?.image1200 || selectedVariant?.image800;

    if (selectedImage) this.#preloadImage(selectedImage).catch(() => {});

    const preloadTask = () => {
      const swatchImages = Array.from(
        this.querySelectorAll('.variant-option--image-swatches input[data-variant-image-800]')
      )
        .slice(0, 6)
        .map((input) => (input instanceof HTMLElement ? input.dataset.variantImage800 : undefined))
        .filter(Boolean);

      swatchImages.forEach((url) => this.#preloadImage(url).catch(() => {}));
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadTask, { timeout: 1200 });
    } else {
      window.setTimeout(preloadTask, 250);
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<HTMLImageElement>}
   */
  #preloadImage(url) {
    if (!url) return Promise.reject(new Error('No image URL'));

    const normalizedUrl = this.#normalizeImageUrl(url);
    const cached = this.#imagePreloadCache.get(normalizedUrl);
    if (cached) return cached;

    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (typeof image.decode === 'function') {
          image.decode().then(() => resolve(image)).catch(() => resolve(image));
        } else {
          resolve(image);
        }
      };
      image.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
      image.src = url;
    });

    this.#imagePreloadCache.set(normalizedUrl, promise);
    return promise;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #normalizeImageUrl(url) {
    try {
      const absoluteUrl = new URL(url, window.location.origin);
      absoluteUrl.searchParams.delete('width');
      return absoluteUrl.toString();
    } catch (_error) {
      return url.replace(/([?&])width=\d+/g, '');
    }
  }

  /**
   * @param {string | null} variantId
   * @param {boolean} isOnProductPage
   * @param {boolean} loadsNewProduct
   * @param {string | undefined} newUrl
   */
  #updateBrowserUrl(variantId, isOnProductPage, loadsNewProduct, newUrl) {
    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct && newUrl) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.#getFieldsets();
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * @param {number} fieldsetIndex
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  /**
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '', 10);
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '', 10);

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldset = this.#getFieldsets()[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex] || [];

        if (fieldset && radios) {
          radios.forEach((radio) => {
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
          });

          const previousIndex = checkedIndices[0];
          this.#checkedIndices[fieldsetIndex] = [inputIndex];

          target.dataset.currentChecked = 'true';
          target.checked = true;

          if (previousIndex !== undefined && previousIndex !== inputIndex && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'true';
            this.#checkedIndices[fieldsetIndex].push(previousIndex);
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      if (!selectedOption) throw new Error('Option not found');

      Array.from(target.options).forEach((option) => option.removeAttribute('selected'));
      selectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${encodeURIComponent(viewParamValue)}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.map(encodeURIComponent).join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.map(encodeURIComponent).join(',')}`);
      } else if (selectedOption.dataset.optionValueId) {
        params.push(`option_values=${encodeURIComponent(selectedOption.dataset.optionValueId)}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      params.unshift(`section_id=${encodeURIComponent(SECTION_ID_MAP[closestSectionId] || '')}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   * @param {string} [morphElementSelector]
   * @param {string} [optionValueId]
   * @param {boolean} [skipPickerMorph]
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '', skipPickerMorph = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: { optionValueId },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript =
          html.querySelector('variant-picker script[data-selected-variant]') ||
          html.querySelector('variant-picker script[type="application/json"]');

        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else if (!skipPickerMorph) {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = this.refs.overflowList;
            if (overflowListAfterMorph instanceof OverflowList) overflowListAfterMorph.showAll();
          }
        }

        const variantData = JSON.parse(textContent);
        const productViewAttr = variantPickerJsonScript
          ?.closest('[view-event-payload]')
          ?.getAttribute('view-event-payload')
          ?.trim();

        deferredEventPromise.resolve({
          variant: productViewAttr
            ? JSON.parse(productViewAttr)?.product?.selectedVariant ?? null
            : null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: variantData && typeof variantData === 'object' ? variantData : null,
          },
        });
      })
      .catch((error) => {
        this.#pendingRequestUrl = undefined;

        if (error.name === 'AbortError') {
          deferredEventPromise.reject(error);
          return;
        }

        deferredEventPromise.reject(error);
        console.error(error);
      });
  }

  /**
   * @typedef {object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * @param {Document | Element} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });

    this.#initializePickerState();
    this.#refreshLocalOptionState();
    this.updateVariantPickerCss();
    this.#preloadInitialMedia();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = this.#getFieldsets();
    const measurements = fieldsets
      .map((_, index) => this.#getFieldsetMeasurements(index))
      .filter((measurement) => measurement !== null);

    measurements.forEach((measurement) => this.#applyFieldsetMeasurements(measurement));
  }

  /**
   * @param {Document} newHtml
   * @param {string} elementSelector
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) throw new Error(`No new element source found for ${elementSelector}`);
    morph(element, newElement);
  }

  /**
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  /**
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked, input[data-single-option]'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * @returns {{name: string, value: string}[]}
   */
  getAllSelectedOptions() {
    const options = [];
    const wrappers = Array.from(this.querySelectorAll('[data-variant-option]')).sort((a, b) => {
      return (
        Number(a.getAttribute('data-option-position') || 0) -
        Number(b.getAttribute('data-option-position') || 0)
      );
    });

    wrappers.forEach((wrapper) => {
      const name = wrapper.getAttribute('data-option-name') || '';
      const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
      const select = wrapper.querySelector('select');
      const hiddenInput = wrapper.querySelector('input[data-single-option]');

      const value =
        checkedInput instanceof HTMLInputElement
          ? checkedInput.value
          : select instanceof HTMLSelectElement
            ? select.value
            : hiddenInput instanceof HTMLInputElement
              ? hiddenInput.value
              : wrapper.getAttribute('data-selected-value') || '';

      if (name && value) options.push({ name, value });
    });

    return options;
  }

  /**
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    if (this.#lastSelectedOptionId) return this.#lastSelectedOptionId;

    const selectedOption = this.selectedOption;
    const optionValueId = selectedOption?.dataset.optionValueId;

    if (!optionValueId) return undefined;
    return optionValueId;
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    const values = [];
    const wrappers = Array.from(this.querySelectorAll('[data-variant-option]')).sort((a, b) => {
      return (
        Number(a.getAttribute('data-option-position') || 0) -
        Number(b.getAttribute('data-option-position') || 0)
      );
    });

    wrappers.forEach((wrapper) => {
      const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
      const select = wrapper.querySelector('select');
      const hiddenInput = wrapper.querySelector('input[data-single-option]');

      const selectedElement =
        checkedInput instanceof HTMLInputElement
          ? checkedInput
          : select instanceof HTMLSelectElement
            ? select.options[select.selectedIndex]
            : hiddenInput instanceof HTMLInputElement
              ? hiddenInput
              : null;

      const optionValueId = selectedElement?.dataset.optionValueId;
      if (optionValueId) values.push(optionValueId);
    });

    return values;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
